import { useEffect, useState, useCallback, useMemo, useRef, type ChangeEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Skeleton,
  LinearProgress,
  Tooltip,
  TextField,
  useTheme,
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Pause,
  Refresh,
  HelpOutline,
  Campaign as CampaignIcon,
  Clear,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { RequirePermission, usePermissions } from '../contexts/PermissionContext';
import api from '../services/api';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import CampaignStatusChip from '../components/CampaignStatusChip';
import CampaignConsumptionDisplay from '../components/CampaignConsumptionDisplay';
import CampaignConcurrencyConflictDialog from '../components/CampaignConcurrencyConflictDialog';
import type { AIAgentProvider, Agent, Campaign, CampaignType } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { formatDateTime } from '../utils/date';
import {
  getCampaignConcurrencyConflict,
  type CampaignConcurrencyConflictPayload,
} from '../utils/campaignConcurrencyConflict';
import {
  CAMPAIGN_TRANSFER_TARGET_FALLBACK_KEY,
  CAMPAIGN_SUSPENDED_REASON_LABEL_KEYS,
  CAMPAIGN_SUSPENDED_REASON_TOOLTIP_KEYS,
  buildCampaignSuspendedReasonI18nOptions,
  getCampaignDisplayStatusState,
} from '../utils/campaignOperationalStatus';

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  processing: 'warning',
  ready: 'success',
  active: 'info',
  running: 'info',
  suspended: 'warning',
  paused: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const CAMPAIGN_TYPE_LABEL_KEYS: Record<CampaignType, string> = {
  ai: 'pages:campaigns.create.basicInfo.campaignTypeAi',
  reverse_ivr: 'pages:campaigns.create.basicInfo.campaignTypeReverseIvr',
};

const FILTER_MULTI_VALUE_SEPARATOR = ',';
const CREATED_BY_AUTOCOMPLETE_LIMIT = 10;
const CREATED_BY_AUTOCOMPLETE_DEBOUNCE_MS = 300;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARACTERS = 3;

interface CampaignFilterOption {
  value: string;
  label: string;
}

interface CampaignCreatorApiOption {
  id: string;
  username?: string | null;
  full_name?: string | null;
  email?: string | null;
}

/**
 * Parses a URL filter value represented as CSV into an ordered unique array.
 */
function parseMultiFilterValue(rawValue: string | null | undefined): string[] {
  if (!rawValue) {
    return [];
  }
  const normalizedValues = rawValue
    .split(FILTER_MULTI_VALUE_SEPARATOR)
    .map((value) => value.trim())
    .filter(Boolean);
  return Array.from(new Set(normalizedValues));
}

/**
 * Serializes selected filter values back to URL-compatible CSV format.
 */
function serializeMultiFilterValue(values: string[]): string | undefined {
  const normalizedValues = values
    .map((value) => value.trim())
    .filter(Boolean);
  if (normalizedValues.length === 0) {
    return undefined;
  }
  return normalizedValues.join(FILTER_MULTI_VALUE_SEPARATOR);
}

export default function CampaignListPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:campaigns.title'));
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [providers, setProviders] = useState<AIAgentProvider[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isAiFilterResourcesLoading, setIsAiFilterResourcesLoading] = useState(false);
  const [areAiFilterResourcesLoaded, setAreAiFilterResourcesLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [concurrencyConflict, setConcurrencyConflict] = useState<CampaignConcurrencyConflictPayload | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const tableRowHoverBg = alpha(theme.palette.primary.main, 0.08);
  const flatButtonSx = {
    textTransform: 'none',
    boxShadow: 'none',
  };
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };
  const compactCellTextSx = {
    fontSize: '0.875rem',
    lineHeight: 1.35,
  };
  
  // Cost visibility permissions
  const { capabilities } = useAuth();
  const { hasPermission } = usePermissions();
  const canViewCostCredits = hasPermission('view_metrics');
  const canViewCostCurrency = hasPermission('view_metrics');

  // Number formatting
  const { formatNumber, formatPercent, formatCurrency, formatAverage } = useNumberFormat();

  // Confirmation dialog hook
  const { confirm, DialogComponent: ConfirmDialogComponent } = useConfirmDialog();

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['status', 'search', 'campaign_type', 'ai_agent_provider_id', 'ai_agent_id', 'created_by'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: '-created_at',
  });

  const statusFilterValues = useMemo(() => parseMultiFilterValue(filters.status), [filters.status]);
  const searchFilterValue = filters.search ?? '';
  const campaignTypeFilterValue = filters.campaign_type ?? '';
  const providerFilterValues = useMemo(() => parseMultiFilterValue(filters.ai_agent_provider_id), [filters.ai_agent_provider_id]);
  const agentFilterValues = useMemo(() => parseMultiFilterValue(filters.ai_agent_id), [filters.ai_agent_id]);
  const createdByFilterValues = useMemo(() => parseMultiFilterValue(filters.created_by), [filters.created_by]);
  const [searchInput, setSearchInput] = useState(searchFilterValue);

  const [createdByOptions, setCreatedByOptions] = useState<CampaignFilterOption[]>([]);
  const [createdBySearchInput, setCreatedBySearchInput] = useState('');
  const [isCreatedByLoading, setIsCreatedByLoading] = useState(false);
  const [isCreatedByOpen, setIsCreatedByOpen] = useState(false);
  const [isProviderFilterOpen, setIsProviderFilterOpen] = useState(false);
  const [isAgentFilterOpen, setIsAgentFilterOpen] = useState(false);
  const createdByRequestIdRef = useRef(0);
  const sortOptionsByLabel = useCallback(
    (options: CampaignFilterOption[]): CampaignFilterOption[] =>
      [...options].sort((optionA, optionB) =>
        optionA.label.localeCompare(optionB.label, i18n.language, { sensitivity: 'base' }),
      ),
    [i18n.language],
  );

  const statusOptions = useMemo(
    () => sortOptionsByLabel(
      Object.keys(statusColors).map((value) => ({
        value,
        label: t(`pages:campaigns.statusLabels.${value}`, value),
      })),
    ),
    [sortOptionsByLabel, t],
  );
  const providerOptions = useMemo(
    () =>
      sortOptionsByLabel(
        providers.map((provider) => ({ value: provider.id, label: provider.name })),
      ),
    [providers, sortOptionsByLabel],
  );
  const agentOptions = useMemo(
    () =>
      agents
        .filter(
          (agent) =>
            providerFilterValues.length === 0
            || (agent.aiAgentProviderId ? providerFilterValues.includes(agent.aiAgentProviderId) : false),
        )
        .map((agent) => ({ value: agent.id, label: agent.name })),
    [agents, providerFilterValues],
  );
  const sortedAgentOptions = useMemo(() => sortOptionsByLabel(agentOptions), [agentOptions, sortOptionsByLabel]);
  const createdByOptionsByValue = useMemo(
    () => new Map(createdByOptions.map((option) => [option.value, option])),
    [createdByOptions],
  );
  const selectedCreatedByOptions = useMemo(
    () =>
      createdByFilterValues.map(
        (value) =>
          createdByOptionsByValue.get(value) ?? {
            value,
            label: value,
          },
      ),
    [createdByFilterValues, createdByOptionsByValue],
  );
  const campaignTypeOptions = useMemo(
    () =>
      sortOptionsByLabel(
        (['ai', 'reverse_ivr'] as CampaignType[]).map((value) => ({
          value,
          label: t(CAMPAIGN_TYPE_LABEL_KEYS[value]),
        })),
      ),
    [sortOptionsByLabel, t],
  );
  const selectedCampaignTypeOption = useMemo(
    () => campaignTypeOptions.find((option) => option.value === campaignTypeFilterValue) ?? null,
    [campaignTypeFilterValue, campaignTypeOptions],
  );

  const orderingParam = ordering || '-created_at';
  const activeOrderingField = orderingParam.startsWith('-') ? orderingParam.slice(1) : orderingParam;
  const activeOrderingDirection: 'asc' | 'desc' = orderingParam.startsWith('-') ? 'desc' : 'asc';
  const hasAiSubscription = capabilities?.hasAiSubscription ?? false;
  const hasReverseIvrSubscription = capabilities?.hasReverseIvrSubscription ?? false;
  const hasOnlyReverseIvrSubscription = hasReverseIvrSubscription && !hasAiSubscription;
  const showConsumptionColumn = !hasOnlyReverseIvrSubscription;
  const showAiFilters = hasAiSubscription;
  const showCampaignTypeFilter = hasAiSubscription && hasReverseIvrSubscription;
  const shouldLoadAiFilterResources =
    showAiFilters &&
    (isProviderFilterOpen
      || isAgentFilterOpen
      || providerFilterValues.length > 0
      || agentFilterValues.length > 0);
  const shouldLoadCreatedByOptions = isCreatedByOpen || createdByFilterValues.length > 0;
  const resolveSelectedOptions = useCallback(
    (selectedValues: string[], options: CampaignFilterOption[]): CampaignFilterOption[] => {
      const optionsByValue = new Map(options.map((option) => [option.value, option]));
      return selectedValues.map(
        (value) =>
          optionsByValue.get(value) ?? {
            value,
            label: value,
          },
      );
    },
    [],
  );
  const handleOrderingChange = useCallback(
    (orderingField: string) => {
      const isCurrentOrderingField = activeOrderingField === orderingField;
      const nextDirection = isCurrentOrderingField && activeOrderingDirection === 'asc' ? 'desc' : 'asc';
      const nextOrdering = nextDirection === 'desc' ? `-${orderingField}` : orderingField;
      setParams({ ordering: nextOrdering }, { resetPage: true });
    },
    [activeOrderingDirection, activeOrderingField, setParams],
  );

  const loadAiFilterResources = useCallback(async () => {
    if (areAiFilterResourcesLoaded || isAiFilterResourcesLoading) {
      return;
    }

    setIsAiFilterResourcesLoading(true);
    try {
      const [providersRes, agentsRes] = await Promise.all([
        api.get('/api/ai-agents-providers/available/'),
        api.get('/api/agents/', { params: { is_active: true } }),
      ]);

      const providerResults = Array.isArray(providersRes.data?.results)
        ? providersRes.data.results
        : (providersRes.data || []);
      const activeProviders = providerResults.filter(
        (provider: AIAgentProvider & { is_active?: boolean }) => provider.isActive || provider.is_active,
      );
      const agentResults = Array.isArray(agentsRes.data?.results)
        ? agentsRes.data.results
        : (agentsRes.data || []);

      setProviders(activeProviders);
      setAgents(agentResults);
      setAreAiFilterResourcesLoaded(true);
    } catch (err) {
      console.error('Error loading campaign list filters data:', err);
      setProviders([]);
      setAgents([]);
      setAreAiFilterResourcesLoaded(false);
    } finally {
      setIsAiFilterResourcesLoading(false);
    }
  }, [areAiFilterResourcesLoaded, isAiFilterResourcesLoading]);

  const loadCreatedByOptions = useCallback(async (searchTerm: string) => {
    const requestId = createdByRequestIdRef.current + 1;
    createdByRequestIdRef.current = requestId;
    setIsCreatedByLoading(true);
    try {
      const response = await api.get('/api/campaigns/created-by-options/', {
        params: {
          search: searchTerm || undefined,
          limit: CREATED_BY_AUTOCOMPLETE_LIMIT,
        },
      });
      if (createdByRequestIdRef.current !== requestId) {
        return;
      }

      const rawOptions: CampaignCreatorApiOption[] = Array.isArray(response.data) ? response.data : [];
      const nextOptions: CampaignFilterOption[] = rawOptions.map((item) => {
        const label = item.full_name || item.username || item.email || item.id;
        return {
          value: item.id,
          label,
        };
      });
      setCreatedByOptions(sortOptionsByLabel(nextOptions));
    } catch (err) {
      if (createdByRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Error loading campaign creators filter options:', err);
      setCreatedByOptions([]);
    } finally {
      if (createdByRequestIdRef.current === requestId) {
        setIsCreatedByLoading(false);
      }
    }
  }, [sortOptionsByLabel]);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const params: Record<string, string | number> = {
        page: page + 1,  // DRF PageNumberPagination uses 1-based pages
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      const statusFilter = serializeMultiFilterValue(statusFilterValues);
      if (statusFilter) {
        params.status = statusFilter;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }
      if (campaignTypeFilterValue) {
        params.campaign_type = campaignTypeFilterValue;
      }
      const providerFilter = serializeMultiFilterValue(providerFilterValues);
      if (providerFilter) {
        params.ai_agent_provider_id = providerFilter;
      }
      const agentFilter = serializeMultiFilterValue(agentFilterValues);
      if (agentFilter) {
        params.ai_agent_id = agentFilter;
      }
      const createdByFilter = serializeMultiFilterValue(createdByFilterValues);
      if (createdByFilter) {
        params.created_by = createdByFilter;
      }

      const response = await api.get('/api/campaigns/', { params });
      
      const data = response.data.results ?? response.data;
      setCampaigns(Array.isArray(data) ? data : []);
      setTotalCampaigns(response.data.count ?? data.length ?? 0);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading campaigns:', err);
      setError(error?.response?.data?.detail || 'Error loading campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    statusFilterValues,
    searchFilterValue,
    campaignTypeFilterValue,
    providerFilterValues,
    agentFilterValues,
    createdByFilterValues,
    orderingParam,
  ]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    setSearchInput(searchFilterValue);
  }, [searchFilterValue]);

  useEffect(() => {
    if (hasAiSubscription) {
      return;
    }
    setProviders([]);
    setAgents([]);
    setAreAiFilterResourcesLoaded(false);
  }, [hasAiSubscription]);

  useEffect(() => {
    if (showCampaignTypeFilter || !campaignTypeFilterValue) {
      return;
    }
    setParams({ campaign_type: null }, { resetPage: true });
  }, [campaignTypeFilterValue, setParams, showCampaignTypeFilter]);

  useEffect(() => {
    if (!shouldLoadCreatedByOptions) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      void loadCreatedByOptions(createdBySearchInput);
    }, CREATED_BY_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [createdBySearchInput, loadCreatedByOptions, shouldLoadCreatedByOptions]);

  useEffect(() => {
    if (!shouldLoadAiFilterResources || areAiFilterResourcesLoaded || isAiFilterResourcesLoading) {
      return;
    }

    void loadAiFilterResources();
  }, [
    areAiFilterResourcesLoaded,
    isAiFilterResourcesLoading,
    loadAiFilterResources,
    shouldLoadAiFilterResources,
  ]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const normalizedSearch = searchInput.trim();
      if (normalizedSearch.length > 0 && normalizedSearch.length < SEARCH_MIN_CHARACTERS) {
        return;
      }

      const nextSearchParam = normalizedSearch.length === 0 ? undefined : normalizedSearch;
      const currentSearchParam = searchFilterValue || undefined;
      if (currentSearchParam === nextSearchParam) {
        return;
      }

      setParams({ search: nextSearchParam }, { resetPage: true });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchFilterValue, searchInput, setParams]);

  const handleStartCampaign = async (id: string) => {
    const confirmed = await confirm({
      title: t('pages:campaigns.startCampaign.title'),
      message: t('pages:campaigns.startCampaign.message'),
      type: 'info',
      icon: <PlayArrow color="success" />,
      confirmIcon: <PlayArrow />,
      confirmText: t('pages:campaigns.startCampaign.confirm'),
    });

    if (!confirmed) return;

    try {
      await api.post(`/api/campaigns/${id}/start/`);
      fetchCampaigns();
    } catch (err: unknown) {
      const conflict = getCampaignConcurrencyConflict(err);
      if (conflict) {
        setConcurrencyConflict(conflict);
        return;
      }
      const error = err as { response?: { data?: { error?: string } } };
      setError(error?.response?.data?.error || 'Error starting campaign');
    }
  };

  const handlePauseCampaign = async (id: string) => {
    const confirmed = await confirm({
      title: t('pages:campaigns.pauseCampaign.title'),
      message: t('pages:campaigns.pauseCampaign.message'),
      type: 'warning',
      icon: <Pause color="warning" />,
      confirmIcon: <Pause />,
      confirmText: t('pages:campaigns.pauseCampaign.confirm'),
    });

    if (!confirmed) return;

    try {
      await api.post(`/api/campaigns/${id}/pause/`);
      fetchCampaigns();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error?.response?.data?.error || 'Error pausing campaign');
    }
  };

  const getProgress = (campaign: Campaign) => {
    // Use the API's progressPercentage which measures contacts no longer pending.
    // This includes terminal outcomes such as skipped after campaign cancellation.
    return campaign.progressPercentage ?? 0;
  };

  const hasActiveFilters = Boolean(
    searchFilterValue ||
    searchInput.trim() ||
    campaignTypeFilterValue ||
    statusFilterValues.length > 0 ||
    providerFilterValues.length > 0 ||
    agentFilterValues.length > 0 ||
    createdByFilterValues.length > 0,
  );
  const selectedStatusOptions = useMemo(
    () => resolveSelectedOptions(statusFilterValues, statusOptions),
    [resolveSelectedOptions, statusFilterValues, statusOptions],
  );
  const selectedProviderOptions = useMemo(
    () => resolveSelectedOptions(providerFilterValues, providerOptions),
    [resolveSelectedOptions, providerFilterValues, providerOptions],
  );
  const selectedAgentOptions = useMemo(
    () => resolveSelectedOptions(agentFilterValues, sortedAgentOptions),
    [resolveSelectedOptions, agentFilterValues, sortedAgentOptions],
  );

  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);
  const columnsCount =
    8 +
    (showConsumptionColumn ? 1 : 0) +
    (canViewCostCurrency ? 1 : 0) +
    (canViewCostCredits ? 1 : 0);

  const headerActions = (
    <>
      <ListActionButton
        startIcon={<Refresh />}
        onClick={fetchCampaigns}
        disabled={loading}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="campaigns_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/campaigns/new"
        >
          {t('pages:campaigns.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  const filterControls = (
    <>
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:campaigns.filters.searchPlaceholder')}
        value={searchInput}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setSearchInput(event.target.value);
        }}
        sx={{ ...filterInputSx, minWidth: 240, flex: '1 1 240px' }}
      />
      {showCampaignTypeFilter && (
        <Autocomplete<CampaignFilterOption, false, false, false>
          options={campaignTypeOptions}
          value={selectedCampaignTypeOption}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, selectedOption) => {
            setParams({ campaign_type: selectedOption?.value ?? null }, { resetPage: true });
          }}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:campaigns.filters.campaignTypeLabel')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      <Autocomplete<CampaignFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={statusOptions}
        value={selectedStatusOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ status: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => (
          <Box component="li" {...props}>
            <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
            {option.label}
          </Box>
        )}
        sx={{ minWidth: 220, flex: '1 1 220px' }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:campaigns.filters.statusLabel')}
            sx={filterInputSx}
          />
        )}
      />
      {showAiFilters && (
        <Autocomplete<CampaignFilterOption, true, false, false>
          multiple
          disableCloseOnSelect
          options={providerOptions}
          loading={isAiFilterResourcesLoading}
          value={selectedProviderOptions}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onOpen={() => {
            setIsProviderFilterOpen(true);
          }}
          onClose={() => {
            setIsProviderFilterOpen(false);
          }}
          onChange={(_, selectedOptions) => {
            const nextProviderValues = selectedOptions.map((option) => option.value);
            setParams(
              {
                ai_agent_provider_id: serializeMultiFilterValue(nextProviderValues),
                ai_agent_id: null,
              },
              { resetPage: true },
            );
          }}
          renderOption={(props, option, { selected }) => (
            <Box component="li" {...props}>
              <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
              {option.label}
            </Box>
          )}
          sx={{ minWidth: 240, flex: '1 1 240px' }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:campaigns.filters.providerLabel')}
              sx={filterInputSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isAiFilterResourcesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
      {showAiFilters && (
        <Autocomplete<CampaignFilterOption, true, false, false>
          multiple
          disableCloseOnSelect
          options={sortedAgentOptions}
          loading={isAiFilterResourcesLoading}
          value={selectedAgentOptions}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onOpen={() => {
            setIsAgentFilterOpen(true);
          }}
          onClose={() => {
            setIsAgentFilterOpen(false);
          }}
          onChange={(_, selectedOptions) => {
            const nextAgentValues = selectedOptions.map((option) => option.value);
            setParams({ ai_agent_id: serializeMultiFilterValue(nextAgentValues) }, { resetPage: true });
          }}
          renderOption={(props, option, { selected }) => (
            <Box component="li" {...props}>
              <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
              {option.label}
            </Box>
          )}
          sx={{ minWidth: 240, flex: '1 1 240px' }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:campaigns.filters.agentLabel')}
              sx={filterInputSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isAiFilterResourcesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
      <Autocomplete<CampaignFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        options={createdByOptions}
        value={selectedCreatedByOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        loading={isCreatedByLoading}
        inputValue={createdBySearchInput}
        onOpen={() => {
          setIsCreatedByOpen(true);
        }}
        onClose={() => {
          setIsCreatedByOpen(false);
        }}
        onInputChange={(_, value, reason) => {
          if (reason === 'reset') {
            return;
          }
          setCreatedBySearchInput(value);
        }}
        onChange={(_, selectedOptions) => {
          const nextCreatedByValues = selectedOptions.map((option) => option.value);
          setParams({ created_by: serializeMultiFilterValue(nextCreatedByValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => (
          <Box component="li" {...props}>
            <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
            {option.label}
          </Box>
        )}
        sx={{ minWidth: 240, flex: '1 1 240px' }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:campaigns.filters.createdByLabel')}
            sx={filterInputSx}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isCreatedByLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Button
        variant="outlined"
        startIcon={<Clear />}
        disabled={!hasActiveFilters}
        onClick={() => {
          if (!hasActiveFilters) return;
          setParams(
            { search: null, status: null, campaign_type: null, ai_agent_provider_id: null, ai_agent_id: null, created_by: null },
            { resetPage: true },
          );
          setSearchInput('');
          setCreatedBySearchInput('');
        }}
        sx={{
          ...flatButtonSx,
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('common:actions.clear')}
      </Button>
    </>
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            },
          }}
        >
          <TableCell>{columnTitle(t('pages:campaigns.name'))}</TableCell>
          <TableCell>{columnTitle(t('pages:campaigns.status'))}</TableCell>
          <TableCell
            align="center"
            sortDirection={activeOrderingField === 'max_concurrent_calls' ? activeOrderingDirection : false}
          >
            <TableSortLabel
              active={activeOrderingField === 'max_concurrent_calls'}
              direction={activeOrderingField === 'max_concurrent_calls' ? activeOrderingDirection : 'asc'}
              onClick={() => handleOrderingChange('max_concurrent_calls')}
            >
              {columnTitle(t('pages:campaigns.concurrency'))}
            </TableSortLabel>
          </TableCell>
          <TableCell
            align="center"
            sortDirection={activeOrderingField === 'progress_percentage' ? activeOrderingDirection : false}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <TableSortLabel
                active={activeOrderingField === 'progress_percentage'}
                direction={activeOrderingField === 'progress_percentage' ? activeOrderingDirection : 'asc'}
                onClick={() => handleOrderingChange('progress_percentage')}
              >
                {columnTitle(t('pages:campaigns.progress'))}
              </TableSortLabel>
              <Tooltip title={t('pages:campaigns.progressTooltip')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell
            align="center"
            sortDirection={activeOrderingField === 'total_contacts' ? activeOrderingDirection : false}
          >
            <TableSortLabel
              active={activeOrderingField === 'total_contacts'}
              direction={activeOrderingField === 'total_contacts' ? activeOrderingDirection : 'asc'}
              onClick={() => handleOrderingChange('total_contacts')}
            >
              {columnTitle(t('pages:campaigns.contacts'))}
            </TableSortLabel>
          </TableCell>
          <TableCell
            align="center"
            sortDirection={activeOrderingField === 'total_phones' ? activeOrderingDirection : false}
          >
            <TableSortLabel
              active={activeOrderingField === 'total_phones'}
              direction={activeOrderingField === 'total_phones' ? activeOrderingDirection : 'asc'}
              onClick={() => handleOrderingChange('total_phones')}
            >
              {columnTitle(t('pages:campaigns.phones'))}
            </TableSortLabel>
          </TableCell>
          <TableCell
            align="center"
            sortDirection={activeOrderingField === 'total_calls' ? activeOrderingDirection : false}
          >
            <TableSortLabel
              active={activeOrderingField === 'total_calls'}
              direction={activeOrderingField === 'total_calls' ? activeOrderingDirection : 'asc'}
              onClick={() => handleOrderingChange('total_calls')}
            >
              {columnTitle(t('pages:campaigns.callsColumn'))}
            </TableSortLabel>
          </TableCell>
          {showConsumptionColumn && (
            <TableCell
              align="center"
              sortDirection={activeOrderingField === 'total_billed_seconds' ? activeOrderingDirection : false}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <TableSortLabel
                  active={activeOrderingField === 'total_billed_seconds'}
                  direction={activeOrderingField === 'total_billed_seconds' ? activeOrderingDirection : 'asc'}
                  onClick={() => handleOrderingChange('total_billed_seconds')}
                >
                  {columnTitle(t('pages:calls.consumption'))}
                </TableSortLabel>
                <Tooltip title={t('pages:campaigns.detail.results.callDurationTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
          )}
          {canViewCostCurrency && (
            <TableCell
              align="right"
              sortDirection={activeOrderingField === 'total_estimated_cost_usd' ? activeOrderingDirection : false}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <TableSortLabel
                  active={activeOrderingField === 'total_estimated_cost_usd'}
                  direction={activeOrderingField === 'total_estimated_cost_usd' ? activeOrderingDirection : 'asc'}
                  onClick={() => handleOrderingChange('total_estimated_cost_usd')}
                >
                  {columnTitle(t('pages:campaigns.costUsd'))}
                </TableSortLabel>
                <Tooltip title={t('pages:campaigns.costTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
          )}
          {canViewCostCredits && (
            <TableCell
              align="right"
              sortDirection={activeOrderingField === 'total_cost_credits' ? activeOrderingDirection : false}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <TableSortLabel
                  active={activeOrderingField === 'total_cost_credits'}
                  direction={activeOrderingField === 'total_cost_credits' ? activeOrderingDirection : 'asc'}
                  onClick={() => handleOrderingChange('total_cost_credits')}
                >
                  {columnTitle(t('pages:campaigns.credits'))}
                </TableSortLabel>
                <Tooltip title={t('pages:campaigns.creditsTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
          )}
          <TableCell align="center">{columnTitle(t('pages:campaigns.actions'))}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          [...Array(5)].map((_, i) => (
            <TableRow key={i}>
              {[...Array(columnsCount)].map((_, j) => (
                <TableCell key={j}><Skeleton /></TableCell>
              ))}
            </TableRow>
          ))
        ) : campaigns.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnsCount} align="center" sx={{ py: 8 }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('pages:campaigns.noCampaigns')}
              </Typography>
              <ListActionButton
                startIcon={<Add />}
                component={RouterLink}
                to="/campaigns/new"
              >
                {t('pages:campaigns.createFirst')}
              </ListActionButton>
            </TableCell>
          </TableRow>
        ) : (
          campaigns.map((campaign) => {
            const progress = getProgress(campaign);
            const { status: displayStatus, suspendedReason } = getCampaignDisplayStatusState(campaign);
            const isSuspended = displayStatus === 'suspended';
            const isRunning = displayStatus === 'running';
            const nextRunLabel = formatDateTime(campaign.nextRunAt, {
              locale: i18n.language,
              fallback: '-',
            });
            const suspendedReasonOptions = buildCampaignSuspendedReasonI18nOptions(campaign, {
              nextRun: nextRunLabel,
              transferTargetFallback: t(CAMPAIGN_TRANSFER_TARGET_FALLBACK_KEY),
            });
            const suspendedReasonLabel = suspendedReason
              ? t(CAMPAIGN_SUSPENDED_REASON_LABEL_KEYS[suspendedReason], suspendedReasonOptions)
              : null;
            const suspendedReasonTooltip = suspendedReason
              ? t(CAMPAIGN_SUSPENDED_REASON_TOOLTIP_KEYS[suspendedReason], suspendedReasonOptions)
              : null;

            return (
              <TableRow
                key={campaign.id}
                hover
                sx={{
                  transition: 'background 0.2s',
                  '&:hover': { backgroundColor: tableRowHoverBg },
                }}
              >
                {/* Name */}
                <LinkTableCell to={`/campaigns/${campaign.id}`}>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        ...compactCellTextSx,
                        '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                      }}
                    >
                      {campaign.name}
                    </Typography>
                    {campaign.description && (
                      <Typography variant="caption" color="text.secondary">
                        {campaign.description.substring(0, 50)}
                        {campaign.description.length > 50 ? '...' : ''}
                      </Typography>
                    )}
                  </Box>
                </LinkTableCell>
                {/* Status */}
                <TableCell>
                  <Box>
                    <CampaignStatusChip
                      status={displayStatus}
                      size="small"
                      description={suspendedReasonTooltip || undefined}
                    />
                    {isSuspended && suspendedReasonLabel && suspendedReasonTooltip && (
                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {suspendedReasonLabel}
                        </Typography>
                        <Tooltip title={suspendedReasonTooltip} arrow>
                          <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                {/* Concurrency */}
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600} sx={compactCellTextSx}>
                    {formatNumber(campaign.maxConcurrentCalls)}
                  </Typography>
                </TableCell>
                {/* Progress */}
                <TableCell sx={{ minWidth: 120 }}>
                  <Tooltip title={t('pages:campaigns.progressTooltip')} arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: isRunning ? 'info.main' : 'success.main',
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
                        {formatPercent(progress)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                {/* Contacts - Total with detailed tooltip */}
                <TableCell align="center">
                  <Tooltip
                    title={
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                          {t('pages:campaigns.contacts')}: {formatNumber(campaign.totalContacts)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <span style={{ color: '#9e9e9e' }}>●</span> {t('pages:campaigns.contactStatus.pending')}: {formatNumber(campaign.pendingContacts)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <span style={{ color: '#4caf50' }}>●</span> {t('pages:campaigns.contactStatus.reached')}: {formatNumber(campaign.reachedContacts ?? 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <span style={{ color: '#00a884' }}>●</span> {t('pages:campaigns.contactStatus.converted')}: {formatNumber(campaign.convertedContacts ?? campaign.convertedCalls ?? 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <span style={{ color: '#9c27b0' }}>●</span> {t('pages:campaigns.contactStatus.optout')}: {formatNumber(campaign.optoutContacts ?? 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <span style={{ color: '#ff9800' }}>●</span> {t('pages:campaigns.contactStatus.mismatch')}: {formatNumber(campaign.mismatchContacts ?? 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <span style={{ color: '#f44336' }}>●</span> {t('pages:campaigns.contactStatus.exhausted')}: {formatNumber(campaign.exhaustedContacts ?? 0)}
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, cursor: 'help' }}>
                      <Typography variant="body2" fontWeight={600} sx={compactCellTextSx}>
                        {formatNumber(campaign.totalContacts)}
                      </Typography>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Box>
                  </Tooltip>
                </TableCell>
                {/* Phones */}
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600} sx={compactCellTextSx}>
                    {formatNumber(campaign.totalPhones ?? campaign.totalContacts)}
                  </Typography>
                </TableCell>
                {/* Calls - Total with detailed tooltip */}
                <TableCell align="center">
                  {(() => {
                    const otherCalls = campaign.pendingCalls ?? Math.max(campaign.totalCalls - campaign.connectedCalls - campaign.failedCalls, 0);
                    return (
                      <Tooltip
                        title={
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                              {t('pages:campaigns.callsColumn')}: {formatNumber(campaign.totalCalls)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              <span style={{ color: '#4caf50' }}>●</span> {t('pages:campaigns.callStatus.connected')}: {formatNumber(campaign.connectedCalls)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              <span style={{ color: '#f44336' }}>●</span> {t('pages:campaigns.callStatus.failed')}: {formatNumber(campaign.failedCalls)}
                            </Typography>
                            {otherCalls > 0 && (
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                <span style={{ color: '#ff9800' }}>●</span> {t('pages:campaigns.callStatus.pending')}/{t('pages:campaigns.callStatus.inProgress')}: {formatNumber(otherCalls)}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, cursor: 'help' }}>
                          <Typography variant="body2" fontWeight={600} sx={compactCellTextSx}>
                            {formatNumber(campaign.totalCalls)}
                          </Typography>
                          <HelpOutline sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                {showConsumptionColumn && (
                  <TableCell align="center">
                    {(() => {
                      const billedSeconds = campaign.totalBilledSeconds ?? campaign.totalDuration ?? 0;
                      if (!billedSeconds) {
                        return '-';
                      }
                      return (
                        <CampaignConsumptionDisplay
                          aiBilledSeconds={campaign.aiBilledSeconds}
                          aiGrossBilledSeconds={campaign.aiGrossBilledSeconds}
                          aiRefundedSeconds={campaign.aiRefundedSeconds}
                          telephonyBilledSeconds={campaign.telephonyBilledSeconds}
                          telephonyGrossBilledSeconds={campaign.telephonyGrossBilledSeconds}
                          telephonyRefundedSeconds={campaign.telephonyRefundedSeconds}
                          aiDurationSeconds={campaign.aiDurationSeconds}
                          telephonyDurationSeconds={campaign.telephonyDurationSeconds}
                          fallbackBilledSeconds={billedSeconds}
                          fallbackDurationSeconds={campaign.totalDuration ?? 0}
                        />
                      );
                    })()}
                  </TableCell>
                )}
                {canViewCostCurrency && (
                  <TableCell align="right">
                    {campaign.totalEstimatedCostUsd ? (
                      <Box>
                        <Typography fontWeight={600} color="info.main">
                          {formatCurrency(Number(campaign.totalEstimatedCostUsd), 'USD')}
                        </Typography>
                        {campaign.avgEstimatedCostUsd && (
                          <Typography variant="caption" color="text.secondary">
                            {formatAverage(Number(campaign.avgEstimatedCostUsd), (value) => formatCurrency(value, 'USD'))}{t('pages:campaigns.perCostedCall')}
                          </Typography>
                        )}
                        {(campaign.costPerMinuteUsd ?? 0) > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatAverage(Number(campaign.costPerMinuteUsd), (value) => formatCurrency(value, 'USD'))}{t('pages:campaigns.perMinute')}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                )}
                {canViewCostCredits && (
                  <TableCell align="right">
                    {campaign.totalCostCredits ? (
                      <Box>
                        <Typography fontWeight={600} color="secondary.main">
                          {formatNumber(Number(campaign.totalCostCredits))}
                        </Typography>
                        {campaign.avgCostCredits && (
                          <Typography variant="caption" color="text.secondary">
                            {formatAverage(Number(campaign.avgCostCredits), formatNumber)}{t('pages:campaigns.perAnsweredCall')}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                )}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {(campaign.status === 'ready' || campaign.status === 'paused') && (
                      <RequirePermission permission="campaigns_control">
                        <Tooltip title={t('pages:campaigns.start')}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleStartCampaign(campaign.id)}
                          >
                            <PlayArrow />
                          </IconButton>
                        </Tooltip>
                      </RequirePermission>
                    )}
                    {(campaign.status === 'active' || campaign.status === 'running') && (
                      <RequirePermission permission="campaigns_control">
                        <Tooltip title={t('pages:campaigns.pause')}>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handlePauseCampaign(campaign.id)}
                          >
                            <Pause />
                          </IconButton>
                        </Tooltip>
                      </RequirePermission>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  const pagination = (
    <LinkPagination
      component="div"
      count={totalCampaigns}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
      }}
      rowsPerPageOptions={[5, 10, 25, 50]}
      labelRowsPerPage={t('common:pagination.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count !== -1
          ? `${from}-${to} ${t('common:common.of')} ${count}`
          : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
      }
      getItemAriaLabel={(type) => {
        if (type === 'first') return t('common:pagination.firstPage');
        if (type === 'last') return t('common:pagination.lastPage');
        if (type === 'next') return t('common:pagination.nextPage');
        return t('common:pagination.previousPage');
      }}
      getPageLink={getPageLink}
    />
  );

  const tableFooter = !loading && campaigns.length > 0 ? pagination : undefined;
  const errorContent = error ? (
    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
      {error}
    </Alert>
  ) : undefined;

  return (
    <Box>
      <FilteredListLayout
        headerProps={{
          icon: <CampaignIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
          title: t('pages:campaigns.title'),
          subtitle: t('pages:campaigns.subtitle'),
          actions: headerActions,
        }}
        filterControls={filterControls}
        table={table}
        tableFooter={tableFooter}
        errorContent={errorContent}
      />

      {/* Confirmation Dialog */}
      {ConfirmDialogComponent}
      <CampaignConcurrencyConflictDialog
        open={Boolean(concurrencyConflict)}
        conflict={concurrencyConflict}
        onClose={() => setConcurrencyConflict(null)}
      />
    </Box>
  );
}
